import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Boxes, ChevronLeft, ChevronRight, FileText, Image as ImageIcon, ImagePlus, IndianRupee, Layers, Loader2,
  Plus, Settings2, Star, Trash2, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import ProductMedia from "./ProductMedia";

const UNIT_OPTIONS = [
  { value: "pc", label: "Piece (pc)" },
  { value: "pack", label: "Pack" },
  { value: "box", label: "Box" },
  { value: "roll", label: "Roll" },
  { value: "set", label: "Set" },
  { value: "kg", label: "Kilogram (kg)" },
  { value: "g", label: "Gram (g)" },
  { value: "L", label: "Litre (L)" },
  { value: "m", label: "Meter (m)" },
];

const ATTRIBUTE_SUGGESTIONS = ["Capacity", "Size", "Dimensions", "Thickness", "Pack", "Weight", "Colour"];

const SECTIONS = [
  { id: "basic", label: "Basic Info", icon: FileText },
  { id: "media", label: "Media", icon: ImageIcon },
  { id: "pricing", label: "Pricing", icon: IndianRupee },
  { id: "inventory", label: "Inventory", icon: Boxes },
  { id: "variants", label: "Variants", icon: Layers },
  { id: "settings", label: "Settings", icon: Settings2 },
];

const EMPTY_VARIANT = { sku: "", label: "", price: "", compare_at_price: "", stock: 50, low_stock_threshold: 10, image: "", images: [], enabled: true, min_order_qty: 1, qty_increment: 1, gst_rate: "", hsn: "" };

const EMPTY_FORM = {
  name: "", category: "", category_id: "", subcategory_id: "", brand_id: "", sub_brand_id: "", unit: "pc", variant_attribute: "", short_description: "", description: "", keywords: "", prime_price: "",
  gst_rate: "", hsn: "",
  images: [], variants: [{ ...EMPTY_VARIANT }],
  active: true, new_arrival: false,
  shipping_info: "", return_policy: "", seed_rating_avg: 0, seed_rating_count: 0,
};

const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500";

function fromProduct(p) {
  return {
    name: p.name, category: p.category, category_id: p.category_id || "", subcategory_id: p.subcategory_id || "", brand_id: p.brand_id || "", sub_brand_id: p.sub_brand_id || "", prime_price: p.prime_price ?? "",
    unit: p.unit || "pc", variant_attribute: p.variant_attribute || "",
    gst_rate: p.gst_rate ?? "", hsn: p.hsn || "",
    short_description: p.short_description || "",
    description: p.description || "", keywords: (p.keywords || []).join(", "),
    images: [...(p.images || [])],
    variants: p.variants.map((v) => ({
      sku: v.sku, label: v.label, price: String(v.price),
      compare_at_price: v.compare_at_price ?? "", stock: v.stock,
      low_stock_threshold: v.low_stock_threshold, image: v.image || "",
      images: [...(v.images && v.images.length ? v.images : (v.image ? [v.image] : []))],
      enabled: v.enabled !== false, min_order_qty: v.min_order_qty || 1,
      qty_increment: v.qty_increment || 1, options: v.options || {},
      price_tiers: v.price_tiers || [],
      gst_rate: v.gst_rate ?? "", hsn: v.hsn || "",
    })),
    active: p.active !== false, new_arrival: !!p.new_arrival,
    shipping_info: p.shipping_info || "", return_policy: p.return_policy || "",
    seed_rating_avg: p.seed_rating_avg || 0, seed_rating_count: p.seed_rating_count || 0,
  };
}

const MEDIA_MAX_BYTES = 5 * 1024 * 1024;

function VariantImages({ images, onChange, productImages, index }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const galleryOptions = productImages.filter((u) => !images.includes(u));

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) return toast.error(`${file.name}: use JPG, PNG or WEBP`);
    if (file.size > MEDIA_MAX_BYTES) return toast.error(`${file.name} is over 5 MB`);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      onChange([...images, data.url]);
    } catch (e) {
      toast.error(apiError(e, `Couldn't upload ${file.name}`));
    } finally {
      setUploading(false);
    }
  };

  const move = (j, dir) => {
    const t = j + dir;
    if (t < 0 || t >= images.length) return;
    const a = [...images];
    [a[j], a[t]] = [a[t], a[j]];
    onChange(a);
  };
  const remove = (j) => onChange(images.filter((_, x) => x !== j));
  const makePrimary = (j) => {
    const a = [...images];
    const [u] = a.splice(j, 1);
    onChange([u, ...a]);
  };

  return (
    <div className="border-t border-white/[0.05] pt-3" data-testid={`variant-images-${index}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Variant images</label>
        <span className="text-[10px] text-neutral-600">Optional — first image is the variant primary; empty falls back to the product gallery</span>
      </div>
      <div className="flex flex-wrap items-start gap-2">
        {images.map((url, j) => (
          <div key={url + j} className="group relative h-16 w-16 overflow-hidden rounded-lg border border-white/10 bg-[#111]" data-testid={`variant-image-thumb-${index}-${j}`}>
            <img src={url} alt="" className="h-full w-full object-contain" />
            {j === 0 && (
              <span className="absolute left-0.5 top-0.5 rounded bg-amber-500 px-1 py-px text-[8px] font-bold text-black" data-testid={`variant-image-primary-badge-${index}`}>PRIMARY</span>
            )}
            <div className="absolute inset-0 flex items-center justify-center gap-0.5 bg-black/75 opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" onClick={() => move(j, -1)} disabled={j === 0} data-testid={`variant-image-left-${index}-${j}`}
                className="rounded p-0.5 text-neutral-300 hover:bg-white/10 disabled:opacity-30" aria-label="Move left"><ChevronLeft className="h-3 w-3" /></button>
              {j !== 0 && (
                <button type="button" onClick={() => makePrimary(j)} data-testid={`variant-image-primary-${index}-${j}`}
                  className="rounded p-0.5 text-amber-400 hover:bg-amber-500/10" aria-label="Make primary"><Star className="h-3 w-3" /></button>
              )}
              <button type="button" onClick={() => remove(j)} data-testid={`variant-image-remove-${index}-${j}`}
                className="rounded p-0.5 text-red-400 hover:bg-red-500/10" aria-label="Remove image"><X className="h-3 w-3" /></button>
              <button type="button" onClick={() => move(j, 1)} disabled={j === images.length - 1} data-testid={`variant-image-right-${index}-${j}`}
                className="rounded p-0.5 text-neutral-300 hover:bg-white/10 disabled:opacity-30" aria-label="Move right"><ChevronRight className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid={`variant-image-upload-${index}`}
          className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/15 text-neutral-500 transition-colors hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin text-amber-500" /> : <ImagePlus className="h-4 w-4" />}
          <span className="text-[8px] font-bold uppercase">Upload</span>
        </button>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden"
          data-testid={`variant-image-file-${index}`}
          onChange={(e) => { [...e.target.files].forEach(upload); e.target.value = ""; }} />
      </div>
      {galleryOptions.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`variant-image-gallery-${index}`}>
          <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-600">From gallery:</span>
          {galleryOptions.map((url) => (
            <button type="button" key={url} onClick={() => onChange([...images, url])}
              data-testid={`variant-image-gallery-add-${index}-${productImages.indexOf(url)}`}
              className="h-9 w-9 overflow-hidden rounded-md border border-white/10 bg-[#111] transition-colors hover:border-amber-500/60" aria-label="Add from product gallery">
              <img src={url} alt="" className="h-full w-full object-contain" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {images.length === 0 && (
        <p className="mt-1.5 text-[10px] text-neutral-600" data-testid={`variant-images-empty-hint-${index}`}>
          No variant images — customers will see the main product gallery for this variant.
        </p>
      )}
    </div>
  );
}

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        <p className="mt-0.5 text-xs text-neutral-500">{desc}</p>
      </div>
      <button
        type="button" role="switch" aria-checked={checked} data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${checked ? "bg-amber-500" : "bg-white/10"}`}
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

export default function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState(() => (product ? fromProduct(product) : EMPTY_FORM));
  const [section, setSection] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [catSaving, setCatSaving] = useState(false);
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [brandSaving, setBrandSaving] = useState(false);
  const [addingSubBrand, setAddingSubBrand] = useState(false);
  const [newSubBrandName, setNewSubBrandName] = useState("");
  const [subBrandSaving, setSubBrandSaving] = useState(false);
  const [addingSub, setAddingSub] = useState(false);
  const [newSubName, setNewSubName] = useState("");
  const [subSaving, setSubSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setTaxonomy(data)).catch(() => {});
  }, []);

  const addCategory = async () => {
    const name = newCatName.trim();
    if (name.length < 2) {
      toast.error("Category name needs at least 2 characters");
      return;
    }
    setCatSaving(true);
    try {
      const { data } = await api.post("/admin/categories", { name });
      setTaxonomy((t) => ({
        ...t,
        categories: [...(t?.categories || []), { ...data, subcategories: data.subcategories || [] }].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setForm((f) => ({ ...f, category_id: data.id, category: data.name, subcategory_id: "" }));
      setNewCatName("");
      setAddingCat(false);
      toast.success(`Category "${data.name}" added`);
    } catch (err) {
      toast.error(apiError(err, "Could not add category"));
    } finally {
      setCatSaving(false);
    }
  };

  const addBrand = async () => {
    const name = newBrandName.trim();
    if (name.length < 2) {
      toast.error("Brand name needs at least 2 characters");
      return;
    }
    setBrandSaving(true);
    try {
      const { data } = await api.post("/admin/brands", { name });
      setTaxonomy((t) => ({
        ...t,
        brands: [...(t?.brands || []), data].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setForm((f) => ({ ...f, brand_id: data.id }));
      setNewBrandName("");
      setAddingBrand(false);
      toast.success(`Brand "${data.name}" added`);
    } catch (err) {
      toast.error(apiError(err, "Could not add brand"));
    } finally {
      setBrandSaving(false);
    }
  };

  const addSubBrand = async () => {
    const name = newSubBrandName.trim();
    if (name.length < 2) {
      toast.error("Sub-brand name needs at least 2 characters");
      return;
    }
    setSubBrandSaving(true);
    try {
      const { data } = await api.post("/admin/brands", { name, parent_id: form.brand_id });
      setTaxonomy((t) => ({ ...t, brands: [...(t?.brands || []), data] }));
      setForm((f) => ({ ...f, sub_brand_id: data.id }));
      setNewSubBrandName("");
      setAddingSubBrand(false);
      toast.success(`Sub-brand "${data.name}" added`);
    } catch (err) {
      toast.error(apiError(err, "Could not add sub-brand"));
    } finally {
      setSubBrandSaving(false);
    }
  };

  const addSubcategory = async () => {
    const name = newSubName.trim();
    if (name.length < 2) {
      toast.error("Subcategory name needs at least 2 characters");
      return;
    }
    setSubSaving(true);
    try {
      const { data } = await api.post("/admin/categories", { name, parent_id: form.category_id });
      setTaxonomy((t) => ({
        ...t,
        categories: (t?.categories || []).map((c) =>
          c.id === form.category_id
            ? { ...c, subcategories: [...(c.subcategories || []), data].sort((a, b) => a.name.localeCompare(b.name)) }
            : c
        ),
      }));
      setForm((f) => ({ ...f, subcategory_id: data.id }));
      setNewSubName("");
      setAddingSub(false);
      toast.success(`Subcategory "${data.name}" added`);
    } catch (err) {
      toast.error(apiError(err, "Could not add subcategory"));
    } finally {
      setSubSaving(false);
    }
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setImages = (updater) => setForm((f) => ({ ...f, images: typeof updater === "function" ? updater(f.images) : updater }));
  const setVariant = (i, key, val) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)) }));

  const totalStock = form.variants.reduce((s, v) => s + (parseInt(v.stock) || 0), 0);

  const jumpForError = (msg) => {
    const m = msg.toLowerCase();
    if (m.includes("image")) setSection("media");
    else if (m.includes("mrp") || m.includes("price")) setSection("pricing");
    else if (m.includes("sku")) setSection("variants");
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) {
      toast.error("Add at least one product image");
      setSection("media");
      return;
    }
    if (form.variants.some((v) => !v.sku.trim() || !v.label.trim() || !(parseFloat(v.price) > 0))) {
      toast.error("Every variant needs a SKU, label and a price above zero");
      setSection("variants");
      return;
    }
    const badMrp = form.variants.find((v) => v.compare_at_price !== "" && parseFloat(v.compare_at_price) < parseFloat(v.price));
    if (badMrp) {
      toast.error(`MRP can't be lower than the price for ${badMrp.sku || badMrp.label}`);
      setSection("pricing");
      return;
    }
    setSaving(true);
    const attr = form.variant_attribute.trim();
    const payload = {
      ...form,
      gst_rate: form.gst_rate === "" ? null : parseFloat(form.gst_rate),
      prime_price: form.prime_price === "" ? null : parseFloat(form.prime_price),
      hsn: form.hsn.trim(),
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      variants: form.variants.map((v) => ({
        sku: v.sku.trim().toUpperCase(),
        label: v.label.trim(),
        price: parseFloat(v.price),
        compare_at_price: v.compare_at_price !== "" ? parseFloat(v.compare_at_price) : null,
        stock: parseInt(v.stock) || 0,
        low_stock_threshold: parseInt(v.low_stock_threshold) || 0,
        enabled: !!v.enabled,
        min_order_qty: parseInt(v.min_order_qty) || 1,
        qty_increment: parseInt(v.qty_increment) || 1,
        image: (v.images && v.images[0]) || v.image || "",
        images: v.images || [],
        options: v.options && Object.keys(v.options).length ? v.options : (attr ? { [attr.toLowerCase()]: v.label.trim() } : {}),
        price_tiers: v.price_tiers || [],
        gst_rate: v.gst_rate === "" ? null : parseFloat(v.gst_rate),
        hsn: (v.hsn || "").trim(),
      })),
    };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
    } catch (err) {
      const msg = apiError(err, "Couldn't save product");
      toast.error(msg);
      jumpForError(msg);
    } finally {
      setSaving(false);
    }
  };

  const variantInput = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none";

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        className="fixed inset-0 z-[85] flex flex-col bg-[#F1EDE3] sm:inset-x-4 sm:top-[3vh] sm:mx-auto sm:max-h-[94vh] sm:max-w-4xl sm:rounded-2xl sm:border sm:border-white/10"
        data-testid="product-editor-modal"
      >
        <div className="flex items-center justify-between border-b border-white/[0.07] p-5 sm:px-7">
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight">{product ? "Edit Product" : "Add Product"}</h3>
            <p className="mt-0.5 text-xs text-neutral-500">{product ? product.name : "Fill each section, then save"}</p>
          </div>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" aria-label="Close" data-testid="product-editor-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1.5 overflow-x-auto border-b border-white/[0.07] px-4 py-3 sm:px-7" data-testid="product-editor-tabs">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`editor-tab-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-bold transition-colors ${
                section === s.id ? "bg-amber-500 text-black" : "text-neutral-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <s.icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          ))}
        </div>

        <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-7" data-lenis-prevent>
            {section === "basic" && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="section-basic">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Product name</label>
                  <input data-testid="product-form-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="PAQVERO Premium Shipping Box" />
                </div>
                <div>
                  <label className={labelCls}>Category</label>
                  <select
                    data-testid="product-form-category"
                    required
                    value={form.category_id}
                    onChange={(e) => {
                      const cat = (taxonomy?.categories || []).find((c) => c.id === e.target.value);
                      setForm((f) => ({ ...f, category_id: e.target.value, category: cat?.name || f.category, subcategory_id: "" }));
                    }}
                    className={inputCls}
                  >
                    <option value="" disabled={!form.category_id && !form.category}>Select category…</option>
                    {(taxonomy?.categories || []).filter((c) => c.active || c.id === form.category_id).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.active ? "" : " (inactive)"}</option>
                    ))}
                    {form.category && !form.category_id && !(taxonomy?.categories || []).some((c) => c.name === form.category) && (
                      <option value="">{form.category} (legacy — not in Categories)</option>
                    )}
                  </select>
                  <p className="mt-1.5 text-[11px] text-neutral-600">Manage the list in Admin → Categories.</p>
                  {!addingCat ? (
                    <button
                      type="button"
                      data-testid="product-form-add-category-toggle"
                      onClick={() => setAddingCat(true)}
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-amber-500 transition-colors hover:text-amber-400"
                    >
                      <Plus className="h-3 w-3" /> New category
                    </button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        data-testid="product-form-new-category-input"
                        autoFocus
                        value={newCatName}
                        onChange={(e) => setNewCatName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); addCategory(); }
                          if (e.key === "Escape") { setAddingCat(false); setNewCatName(""); }
                        }}
                        maxLength={60}
                        placeholder="Type category name…"
                        className="h-9 w-full rounded-lg border border-amber-500/40 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/70 focus:outline-none"
                      />
                      <button
                        type="button"
                        data-testid="product-form-new-category-submit"
                        onClick={addCategory}
                        disabled={catSaving}
                        className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-3 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
                      >
                        {catSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                      </button>
                      <button
                        type="button"
                        data-testid="product-form-new-category-cancel"
                        onClick={() => { setAddingCat(false); setNewCatName(""); }}
                        className="flex h-9 shrink-0 items-center rounded-lg border border-white/15 px-2.5 text-neutral-400 transition-colors hover:text-white"
                        aria-label="Cancel"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Brand <span className="normal-case text-neutral-600">(optional)</span></label>
                  <select
                    data-testid="product-form-brand"
                    value={form.brand_id}
                    onChange={(e) => setForm((f) => ({ ...f, brand_id: e.target.value, sub_brand_id: "" }))}
                    className={inputCls}
                  >
                    <option value="">No brand</option>
                    {(taxonomy?.brands || []).filter((b) => b.active || b.id === form.brand_id).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}{b.active ? "" : " (inactive)"}</option>
                    ))}
                  </select>
                  {!addingBrand ? (
                    <button
                      type="button"
                      data-testid="product-form-add-brand-toggle"
                      onClick={() => setAddingBrand(true)}
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-amber-500 transition-colors hover:text-amber-400"
                    >
                      <Plus className="h-3 w-3" /> New brand
                    </button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        data-testid="product-form-new-brand-input"
                        autoFocus
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); addBrand(); }
                          if (e.key === "Escape") { setAddingBrand(false); setNewBrandName(""); }
                        }}
                        maxLength={60}
                        placeholder="Type brand name…"
                        className="h-9 w-full rounded-lg border border-amber-500/40 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/70 focus:outline-none"
                      />
                      <button
                        type="button"
                        data-testid="product-form-new-brand-submit"
                        onClick={addBrand}
                        disabled={brandSaving}
                        className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-3 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
                      >
                        {brandSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                      </button>
                      <button
                        type="button"
                        data-testid="product-form-new-brand-cancel"
                        onClick={() => { setAddingBrand(false); setNewBrandName(""); }}
                        className="flex h-9 shrink-0 items-center rounded-lg border border-white/15 px-2.5 text-neutral-400 transition-colors hover:text-white"
                        aria-label="Cancel"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {form.brand_id && (
                    <div className="mt-3">
                      <label className={labelCls}>Sub-brand <span className="normal-case text-neutral-600">(optional)</span></label>
                      <select
                        data-testid="product-form-sub-brand"
                        value={form.sub_brand_id}
                        onChange={(e) => set("sub_brand_id", e.target.value)}
                        className={inputCls}
                      >
                        <option value="">None</option>
                        {(taxonomy?.brands || []).filter((b) => b.parent_id === form.brand_id && (b.active || b.id === form.sub_brand_id)).map((b) => (
                          <option key={b.id} value={b.id}>{b.name}{b.active ? "" : " (inactive)"}</option>
                        ))}
                      </select>
                      {!addingSubBrand ? (
                        <button
                          type="button"
                          data-testid="product-form-add-sub-brand-toggle"
                          onClick={() => setAddingSubBrand(true)}
                          className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-amber-500 transition-colors hover:text-amber-400"
                        >
                          <Plus className="h-3 w-3" /> New sub-brand
                        </button>
                      ) : (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            data-testid="product-form-new-sub-brand-input"
                            autoFocus
                            value={newSubBrandName}
                            onChange={(e) => setNewSubBrandName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); addSubBrand(); }
                              if (e.key === "Escape") { setAddingSubBrand(false); setNewSubBrandName(""); }
                            }}
                            maxLength={60}
                            placeholder="Type sub-brand name…"
                            className="h-9 w-full rounded-lg border border-amber-500/40 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/70 focus:outline-none"
                          />
                          <button
                            type="button"
                            data-testid="product-form-new-sub-brand-submit"
                            onClick={addSubBrand}
                            disabled={subBrandSaving}
                            className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-3 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
                          >
                            {subBrandSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                          </button>
                          <button
                            type="button"
                            data-testid="product-form-new-sub-brand-cancel"
                            onClick={() => { setAddingSubBrand(false); setNewSubBrandName(""); }}
                            className="flex h-9 shrink-0 items-center rounded-lg border border-white/15 px-2.5 text-neutral-400 transition-colors hover:text-white"
                            aria-label="Cancel"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Subcategory <span className="normal-case text-neutral-600">(optional)</span></label>
                  <select
                    data-testid="product-form-subcategory"
                    value={form.subcategory_id}
                    onChange={(e) => set("subcategory_id", e.target.value)}
                    disabled={!form.category_id}
                    className={`${inputCls} disabled:opacity-40`}
                  >
                    <option value="">None</option>
                    {(taxonomy?.categories.find((c) => c.id === form.category_id)?.subcategories || [])
                      .filter((s) => s.active || s.id === form.subcategory_id)
                      .map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactive)"}</option>)}
                  </select>
                  {!addingSub ? (
                    <button
                      type="button"
                      data-testid="product-form-add-subcategory-toggle"
                      onClick={() => setAddingSub(true)}
                      disabled={!form.category_id}
                      title={form.category_id ? "Add a subcategory under the selected category" : "Pick a category first"}
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-amber-500 transition-colors hover:text-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-3 w-3" /> New subcategory
                    </button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        data-testid="product-form-new-subcategory-input"
                        autoFocus
                        value={newSubName}
                        onChange={(e) => setNewSubName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); addSubcategory(); }
                          if (e.key === "Escape") { setAddingSub(false); setNewSubName(""); }
                        }}
                        maxLength={60}
                        placeholder="Type subcategory name…"
                        className="h-9 w-full rounded-lg border border-amber-500/40 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/70 focus:outline-none"
                      />
                      <button
                        type="button"
                        data-testid="product-form-new-subcategory-submit"
                        onClick={addSubcategory}
                        disabled={subSaving}
                        className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-3 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
                      >
                        {subSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                      </button>
                      <button
                        type="button"
                        data-testid="product-form-new-subcategory-cancel"
                        onClick={() => { setAddingSub(false); setNewSubName(""); }}
                        className="flex h-9 shrink-0 items-center rounded-lg border border-white/15 px-2.5 text-neutral-400 transition-colors hover:text-white"
                        aria-label="Cancel"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Selling unit</label>
                  <input
                    data-testid="product-form-unit"
                    list="unit-suggestions"
                    value={form.unit}
                    onChange={(e) => set("unit", e.target.value)}
                    className={inputCls}
                    placeholder="pc, pack, roll… or create your own"
                  />
                  <datalist id="unit-suggestions">
                    {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </datalist>
                  <p className="mt-1.5 text-[11px] text-neutral-600">Pick a standard unit or type a custom one (e.g. dozen, tray, carton). Prices show as ₹9.99/{form.unit || "pc"}.</p>
                </div>
                <div>
                  <label className={labelCls}>GST rate % (default)</label>
                  <input data-testid="product-form-gst-rate" type="number" min="0" max="28" step="0.5" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={inputCls} placeholder="18" />
                  <p className="mt-1.5 text-[11px] text-neutral-600">Blank = 18%. Confirm rates with PAQVERO's CA before changing. Variants can override below.</p>
                </div>
                <div>
                  <label className={labelCls}>HSN / SAC (default)</label>
                  <input data-testid="product-form-hsn" value={form.hsn} onChange={(e) => set("hsn", e.target.value)} className={inputCls} placeholder="e.g. 4819 — enter after CA confirmation" />
                </div>
                <div>
                  <label className={labelCls}>PRIME member price (₹/{form.unit || "pc"}) <span className="normal-case text-neutral-600">(optional)</span></label>
                  <input data-testid="product-prime-price" type="number" min="0" step="0.01" value={form.prime_price} onChange={(e) => set("prime_price", e.target.value)} className={inputCls} placeholder="Leave blank for no member price" />
                  <p className="mt-1.5 text-[11px] text-neutral-600">Active PAQVERO PRIME members pay this flat per-unit price on any variant (never above the normal price).</p>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Keywords (comma separated)</label>
                  <input data-testid="product-form-keywords" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} className={inputCls} placeholder="box, corrugated, shipping" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Short description</label>
                  <input data-testid="product-form-short-desc" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className={inputCls} placeholder="One line that sells it" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Full description</label>
                  <textarea data-testid="product-form-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} h-auto py-3`} />
                </div>
              </div>
            )}

            {section === "media" && (
              <div data-testid="section-media">
                <ProductMedia images={form.images} setImages={setImages} />
              </div>
            )}

            {section === "pricing" && (
              <div data-testid="section-pricing">
                <p className="mb-4 text-xs text-neutral-500">Set the selling price and MRP per {form.unit || "pc"} for each variant. MRP must be greater than or equal to the price.</p>
                <div className="space-y-3">
                  {form.variants.map((v, i) => {
                    const price = parseFloat(v.price) || 0;
                    const mrp = parseFloat(v.compare_at_price) || 0;
                    const off = mrp > price && price > 0 ? Math.round((1 - price / mrp) * 100) : 0;
                    const invalid = v.compare_at_price !== "" && mrp < price;
                    return (
                      <div key={i} className="grid grid-cols-2 items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 sm:grid-cols-[1fr_120px_120px_90px_110px_80px]" data-testid={`pricing-row-${i}`}>
                        <div className="col-span-2 sm:col-span-1">
                          <p className="text-sm font-semibold text-white">{v.label || `Variant ${i + 1}`}</p>
                          <p className="font-mono text-[11px] text-neutral-500">{v.sku || "no SKU"}</p>
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Price ₹/{form.unit || "pc"}</label>
                          <input data-testid={`pricing-price-${i}`} type="number" min="0.01" step="0.01" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className={variantInput} placeholder="0" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">MRP ₹/{form.unit || "pc"}</label>
                          <input data-testid={`pricing-mrp-${i}`} type="number" min="0" step="0.01" value={v.compare_at_price} onChange={(e) => setVariant(i, "compare_at_price", e.target.value)} className={`${variantInput} ${invalid ? "border-red-500/60" : ""}`} placeholder="—" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">GST %</label>
                          <input data-testid={`pricing-gst-${i}`} type="number" min="0" max="28" step="0.5" value={v.gst_rate} onChange={(e) => setVariant(i, "gst_rate", e.target.value)} className={variantInput} placeholder={form.gst_rate || "18"} />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">HSN</label>
                          <input data-testid={`pricing-hsn-${i}`} value={v.hsn} onChange={(e) => setVariant(i, "hsn", e.target.value)} className={variantInput} placeholder={form.hsn || "—"} />
                        </div>
                        <div className="col-span-2 flex items-end justify-end sm:col-span-1">
                          {invalid ? (
                            <span className="text-[11px] font-bold text-red-400" data-testid={`pricing-error-${i}`}>MRP below price</span>
                          ) : off > 0 ? (
                            <span className="rounded-md border border-emerald-500/40 px-2 py-1 text-[11px] font-bold text-emerald-400" data-testid={`pricing-discount-${i}`}>{off}% off</span>
                          ) : <span className="text-[11px] text-neutral-600">no discount</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {section === "inventory" && (
              <div data-testid="section-inventory">
                <div className="mb-4 flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Total stock</span>
                  <span className={`text-lg font-bold ${totalStock <= 0 ? "text-red-400" : "text-emerald-400"}`} data-testid="inventory-total-stock">
                    {totalStock.toLocaleString("en-IN")} <span className="text-xs font-semibold text-neutral-500">{form.unit || "pc"}</span>
                  </span>
                </div>
                <div className="space-y-3">
                  {form.variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-2 items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 sm:grid-cols-[1fr_105px_120px_105px_105px]" data-testid={`inventory-row-${i}`}>
                      <div className="col-span-2 sm:col-span-1">
                        <p className="text-sm font-semibold text-white">{v.label || `Variant ${i + 1}`}</p>
                        <p className="font-mono text-[11px] text-neutral-500">{v.sku || "no SKU"}</p>
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Stock</label>
                        <input data-testid={`inventory-stock-${i}`} type="number" min="0" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)} className={variantInput} />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Low-stock alert ≤</label>
                        <input data-testid={`inventory-threshold-${i}`} type="number" min="0" value={v.low_stock_threshold} onChange={(e) => setVariant(i, "low_stock_threshold", e.target.value)} className={variantInput} />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Min order qty</label>
                        <input data-testid={`inventory-moq-${i}`} type="number" min="1" value={v.min_order_qty} onChange={(e) => setVariant(i, "min_order_qty", e.target.value)} className={variantInput} />
                      </div>
                      <div className="flex items-end pb-2">
                        <p className="text-[10px] leading-relaxed text-neutral-600">Customers buy in multiples of the min order qty (50 → 50, 100, 150…)</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-neutral-600">
                  Quantities count in {form.unit || "pc"} and step by the min order qty. Example: min order 100 lets customers buy 100, 200, 300… {form.unit === "pc" ? "pcs" : `${form.unit || "pc"}`}.
                </p>
              </div>
            )}

            {section === "variants" && (
              <div data-testid="section-variants">
                <div className="mb-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <label className={labelCls}>Variant attribute</label>
                  <input
                    data-testid="product-form-attribute"
                    list="attribute-suggestions"
                    value={form.variant_attribute}
                    onChange={(e) => set("variant_attribute", e.target.value)}
                    className={inputCls}
                    placeholder="Capacity, Size, Dimensions, Thickness…"
                  />
                  <datalist id="attribute-suggestions">
                    {ATTRIBUTE_SUGGESTIONS.map((a) => <option key={a} value={a} />)}
                  </datalist>
                  <p className="mt-1.5 text-[11px] text-neutral-600">
                    Shown as the option group title on the product page (e.g. CAPACITY: 250 ml · 450 ml · 500 ml). Leave empty for simple variants.
                  </p>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-xs text-neutral-500">SKUs are uppercased automatically and must be unique across the whole catalog.</p>
                  <button
                    type="button"
                    data-testid="product-form-add-variant"
                    onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT }] }))}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-500/40 px-3 py-1.5 text-xs font-bold text-amber-400 hover:bg-amber-500/10"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Variant
                  </button>
                </div>
                <div className="space-y-3">
                  {form.variants.map((v, i) => (
                    <div key={i} className={`rounded-xl border p-4 ${v.enabled ? "border-white/[0.07] bg-white/[0.02]" : "border-white/[0.04] bg-transparent opacity-60"}`} data-testid={`variant-row-${i}`}>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[150px_1fr_88px_40px]">
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">SKU</label>
                        <input data-testid={`variant-sku-${i}`} placeholder="DD-450" value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} className={`${variantInput} font-mono`} />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">{form.variant_attribute ? `${form.variant_attribute} value` : "Label"}</label>
                        <input data-testid={`variant-label-${i}`} placeholder={form.variant_attribute ? "e.g. 450 ml" : "Small · 25×20×15 cm"} value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} className={variantInput} />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Enabled</label>
                        <button
                          type="button" role="switch" aria-checked={v.enabled} data-testid={`variant-enabled-${i}`}
                          onClick={() => setVariant(i, "enabled", !v.enabled)}
                          className={`relative h-6 w-10 rounded-full transition-colors ${v.enabled ? "bg-emerald-500" : "bg-white/10"}`}
                        >
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${v.enabled ? "left-[18px]" : "left-0.5"}`} />
                        </button>
                      </div>
                      <div className="flex items-end justify-end">
                        {form.variants.length > 1 && (
                          <button type="button" onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }))} className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-600 hover:text-red-400" aria-label="Remove variant" data-testid={`variant-remove-${i}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      </div>
                      <VariantImages
                        images={v.images || []}
                        onChange={(imgs) => setVariant(i, "images", imgs)}
                        productImages={form.images}
                        index={i}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {section === "settings" && (
              <div className="space-y-3" data-testid="section-settings">
                <Toggle label="Live in shop" desc="Customers can see and buy this product" checked={form.active} onChange={(v) => set("active", v)} testid="product-form-active" />
                <Toggle label="New Arrival badge" desc="Shows the NEW flag on the product card" checked={form.new_arrival} onChange={(v) => set("new_arrival", v)} testid="product-form-new_arrival" />
                <div>
                  <label className={labelCls}>Shipping info</label>
                  <textarea data-testid="product-form-shipping" rows={3} value={form.shipping_info} onChange={(e) => set("shipping_info", e.target.value)} className={`${inputCls} h-auto py-3`} placeholder="Dispatched within 24 hours…" />
                </div>
                <div>
                  <label className={labelCls}>Return policy</label>
                  <textarea data-testid="product-form-returns" rows={3} value={form.return_policy} onChange={(e) => set("return_policy", e.target.value)} className={`${inputCls} h-auto py-3`} placeholder="30-day easy returns…" />
                </div>
                {!product && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Starting rating (0–5)</label>
                      <input data-testid="product-form-seed-rating" type="number" min="0" max="5" step="0.1" value={form.seed_rating_avg} onChange={(e) => set("seed_rating_avg", parseFloat(e.target.value) || 0)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Starting review count</label>
                      <input data-testid="product-form-seed-count" type="number" min="0" value={form.seed_rating_count} onChange={(e) => set("seed_rating_count", parseInt(e.target.value) || 0)} className={inputCls} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-white/[0.07] p-4 sm:px-7">
            <button
              data-testid="product-form-save"
              type="submit"
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {product ? "Save Changes" : "Create Product"}
            </button>
          </div>
        </form>
      </motion.div>
    </>
  );
}
